import { persons } from "@/mocks/archive";
import Reveal from "./Reveal";
import SectionHead from "./SectionHead";

function StageRow({ stages }: { stages: { year: string; title: string }[] }) {
  return (
    <ol className="relative flex flex-col gap-3 border-l border-background-200 pl-4 md:flex-row md:gap-0 md:border-l-0 md:pl-0 md:pt-4">
      <span aria-hidden="true" className="absolute left-0 top-1 h-2 w-2 -translate-x-1/2 rounded-full bg-primary-500 md:-top-0.5 md:left-2"></span>
      {stages.map((s, i) => (
        <li key={s.year} className="relative flex gap-3 md:flex-1 md:flex-col md:gap-2">
          {i !== 0 && (
            <span
              aria-hidden="true"
              className="absolute left-[-21px] top-1 hidden h-0.5 w-4 bg-background-200 md:left-0 md:top-2 md:block"
            />
          )}
          <span className="flex h-6 w-fit shrink-0 items-center rounded-md bg-accent-100 px-2 text-[11px] font-bold text-accent-900">
            {s.year}
          </span>
          <p className="text-xs leading-5 text-foreground-600 md:mt-1 md:text-[13px] md:leading-6">
            {s.title}
          </p>
        </li>
      ))}
    </ol>
  );
}

function BigCard({ p }: { p: (typeof persons)[number] }) {
  const tone = p.accent === "primary" ? "bg-primary-600" : "bg-accent-600";
  const isPrimary = p.accent === "primary";
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-50 lg:flex-row">
      <div className={`relative flex flex-col justify-between p-6 text-background-50 md:p-8 lg:w-[300px] lg:shrink-0 ${tone}`}>
        <div>
          <div className="text-[11px] font-semibold tracking-[0.25em] opacity-80">{p.code}</div>
          <h5 className="mt-6 font-heading text-2xl font-black leading-tight md:text-3xl">{p.name}</h5>
          <p className="mt-2 text-sm opacity-90">{p.role}</p>
        </div>
        <div className="mt-6 flex items-end justify-between">
          <div>
            <div className="text-[11px] opacity-80">时间跨度</div>
            <div className="font-heading text-lg font-bold">{p.span}</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-4xl font-black leading-none">{p.count}</div>
            <div className="text-[11px] opacity-80">关联事件</div>
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-6 md:p-8">
        <p className="font-heading text-base font-bold text-foreground-950 md:text-lg">{p.line}</p>
        <div className="mt-6">
          <StageRow stages={p.stages} />
        </div>
        <a
          href="#deep"
          className={`mt-6 inline-flex w-fit items-center gap-1.5 whitespace-nowrap text-sm font-semibold ${isPrimary ? "text-primary-700" : "text-accent-900"}`}
        >
          <i className={`ri-file-list-3-line ${isPrimary ? "text-primary-600" : "text-accent-700"}`}></i>
          进此人深扒档案
          <i className="ri-arrow-right-line"></i>
        </a>
      </div>
    </div>
  );
}

function SmallCard({ p, idx }: { p: (typeof persons)[number]; idx: number }) {
  const last = p.stages[p.stages.length - 1];
  return (
    <a
      href="#deep"
      className="group flex h-full flex-col rounded-lg border border-background-200/80 bg-background-50 p-5 transition-colors hover:border-primary-300 hover:bg-background-100"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold tracking-wider text-foreground-400">{p.code}</div>
          <h5 className="mt-1 font-heading text-lg font-bold text-foreground-950 group-hover:text-primary-700">
            {p.name}
          </h5>
          <p className="mt-0.5 text-xs text-foreground-500">{p.role}</p>
        </div>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-base font-black ${idx % 3 === 1 ? "bg-accent-100 text-accent-900" : "bg-primary-100 text-primary-800"}`}>
          {String(idx + 1).padStart(2, "0")}
        </span>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <span className="text-[11px] text-foreground-500">最近一档</span>
        <span className="rounded-md bg-background-200/70 px-2 py-0.5 text-[11px] font-medium text-foreground-700">
          {last.year}
        </span>
      </div>
      <p className="mt-2 flex-1 text-[13px] leading-5 text-foreground-600">{last.title}</p>
      <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-3 text-xs">
        <span className="text-foreground-500">{p.span}</span>
        <span className="font-semibold text-primary-700">{p.count} 条 / 跟档案</span>
      </div>
    </a>
  );
}

export default function PersonTimeline() {
  const big = persons.slice(0, 2);
  const small = persons.slice(2);

  return (
    <section id="people" className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
      <Reveal>
        <SectionHead
          kicker="以人为轴 · 纵深一条线"
          title="国产黑料人物梳理"
          desc="同一个人的旧账新料并成一张时间表，谁在哪个年份动了哪一步，逐格对齐一次看全。"
        />
      </Reveal>

      <div className="flex flex-col gap-5">
        {big.map((p, i) => (
          <Reveal key={p.code} delay={i * 80}>
            <BigCard p={p} />
          </Reveal>
        ))}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
          {small.map((p, idx) => (
            <Reveal key={p.code} delay={(idx % 3) * 70}>
              <SmallCard p={p} idx={idx} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}